import React, { useState, useEffect, useMemo } from 'react'
import { Row, Col, Container, Button, Badge, Spinner, Tab, Nav, Modal, Form } from 'react-bootstrap'
import axios from 'axios'
import Card from '../../../components/Card'
import { Link } from 'react-router-dom'
import Moment from 'moment';
import { apiHandler } from '../../../api/apihandler';
import { CLASS_TRANSACTIONS, CLASS_TRANSACTION_PAY, GRADE_TRANSACTIONS, GRADE_TRANSACTION_PAY, QUICKBOOK_CARD_LIST, QUICKBOOK_CARD_SAVE } from "../../../api/constants";
import { useSelector } from 'react-redux';
import { getEffectiveStudentId } from '../../../utilities/teacherModulePermissions';
import Swal from 'sweetalert2'

//profile-header
import ProfileHeader from '../../../components/profile-header'

import img6 from '../../../assets/images/page-img/profile-bg6.jpg'
import NoDataFound from '../../../components/noDataFound';

const MyTransactions = () => {
    let profileData = (useSelector((state) => state.setting.setting.user_data))
    profileData = profileData ? JSON.parse(profileData) : "";
    const [classTransactions, setClassTransactions] = useState([]);
    const [gradeTransactions, setGradeTransactions] = useState([]);
    const [showMoreDataLoader, setShowMoreDataLoader] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [classRefreshing, setClassRefreshing] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [payModalType, setPayModalType] = useState('grade');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState('');
    const [isPaying, setIsPaying] = useState(false);
    const [payModalView, setPayModalView] = useState('pay');
    const [newCardHolder, setNewCardHolder] = useState('');
    const [newCardNumber, setNewCardNumber] = useState('');
    const [newCardExpMonth, setNewCardExpMonth] = useState('01');
    const [newCardExpYear, setNewCardExpYear] = useState(String(new Date().getFullYear()));
    const [newCardCvv, setNewCardCvv] = useState('');
    const [newCardZip, setNewCardZip] = useState('');
    const [isAddCardSubmitting, setIsAddCardSubmitting] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        getGradeTransactionsApi()
        getClassTransactionsApi()
    }, []);

    const summary = useMemo(() => {
        const totalTransactions = gradeTransactions.length;
        const totalPaid = gradeTransactions.reduce((total, item) => total + Number(item?.total_amount || item?.price || 0), 0);
        const totalSubsidy = gradeTransactions.reduce((total, item) => total + Number(item?.subsidy_amount || 0), 0);
        const pendingCount = gradeTransactions.filter((item) => Number(item?.status) !== 1).length;
        return { totalTransactions, totalPaid, totalSubsidy, pendingCount };
    }, [gradeTransactions]);

    const classSummary = useMemo(() => {
        const totalTransactions = classTransactions.length;
        const totalPaid = classTransactions.reduce((total, item) => total + Number(item?.total_amount || item?.price || 0), 0);
        const pendingCount = classTransactions.filter((item) => Number(item?.status) !== 1).length;
        return { totalTransactions, totalPaid, pendingCount };
    }, [classTransactions]);

    const getPaymentTypeLabel = (paymentType) => {
        if (Number(paymentType) === 0) return 'Weekly Payment';
        if (Number(paymentType) === 1) return 'Monthly Payment';
        return 'One Time Payment';
    };

    const getStatusMeta = (transaction) => {
        if (transaction?.transaction_id) {
            return { label: 'Paid', variant: 'success', icon: 'verified' };
        }
        if (Number(transaction?.status) === 1) {
            return { label: 'Completed', variant: 'success', icon: 'check_circle' };
        }
        return { label: 'Pending', variant: 'warning', icon: 'schedule' };
    };

    const isUnpaidTransaction = (transaction) => !transaction?.transaction_id && Number(transaction?.status) !== 1;
    const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;
    const ACCENT = '#d97745';
    const payNowButtonStyle = {
        border: '1px solid #d97745',
        background: '#fff7ed',
        color: '#d97745',
        borderRadius: '14px',
        padding: '4px 12px',
        fontSize: '11px',
        fontWeight: 700,
        boxShadow: 'none',
        outline: 'none'
    };

    const getNormalizedQuickbookCards = (res) => {
        const source = res?.data?.data?.user_quickbook_cards || res?.data?.data?.cards || res?.data?.data?.data || [];
        const list = Array.isArray(source) ? source : (Array.isArray(source?.data) ? source.data : []);

        return list
            .filter((card) => Number(card?.status ?? 1) === 1)
            .map((card) => ({
                id: card?.id || card?.user_quickbook_card_id || card?.userQuickbookCardId,
                quickbookCardId: card?.quickbook_card_id || card?.quickbookCardId,
                name: card?.name,
                expYear: card?.exp_year || card?.expYear,
                expMonth: card?.exp_month || card?.expMonth,
                last4: card?.last4 || card?.card_last4,
                brand: card?.brand || card?.card_brand,
            }))
            .filter((card) => card?.id);
    };

    const getSavedQuickbookCards = async () => {
        const res = await apiHandler(QUICKBOOK_CARD_LIST, 'POST', { student_id: getEffectiveStudentId(profileData) });
        if (res?.data?.response?.status !== 200) {
            return [];
        }
        return getNormalizedQuickbookCards(res);
    };

    const formatCardInput = (value) => {
        const raw = value.replace(/\D/g, '').slice(0, 16);
        return raw.replace(/(.{4})/g, '$1 ').trim();
    };

    const openPayModal = async (transaction, type = 'grade') => {
        const cards = await getSavedQuickbookCards();
        setSavedCards(cards);
        setSelectedCardId(String(cards[0]?.id || ''));
        setPayModalView(cards.length ? 'pay' : 'add-card');
        setNewCardHolder('');
        setNewCardNumber('');
        setNewCardExpMonth('01');
        setNewCardExpYear(String(new Date().getFullYear()));
        setNewCardCvv('');
        setNewCardZip('');
        setPayModalType(type);
        setSelectedTransaction(transaction);
        setShowPayModal(true);
    };

    const closePayModal = () => {
        if (isPaying || isAddCardSubmitting) return;
        setShowPayModal(false);
        setSelectedTransaction(null);
        setSavedCards([]);
        setSelectedCardId('');
        setPayModalView('pay');
        setPayModalType('grade');
    };

    const addCardAndContinue = async () => {
        if (!newCardHolder.trim()) {
            Swal.fire({ title: 'Required', text: 'Please enter card holder name.', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        const cleanNumber = newCardNumber.replace(/\s/g, '');
        if (cleanNumber.length < 12) {
            Swal.fire({ title: 'Required', text: 'Please enter a valid card number.', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        if (!newCardCvv.trim() || newCardCvv.trim().length < 3) {
            Swal.fire({ title: 'Required', text: 'Please enter a valid CVV.', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        setIsAddCardSubmitting(true);
        try {
            const tokenApiUrl = process.env.REACT_APP_QUICKBOOK_TOKEN_URL || 'https://api.intuit.com/quickbooks/v4/payments/tokens';
            const tokenResponse = await axios.post(
                tokenApiUrl,
                {
                    card: {
                        name: newCardHolder.trim(),
                        number: cleanNumber,
                        expMonth: newCardExpMonth,
                        expYear: newCardExpYear,
                        cvc: newCardCvv.trim(),
                        address: { postalCode: newCardZip, country: 'US' }
                    }
                },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const quickbookToken = tokenResponse?.data?.value;
            if (!quickbookToken) {
                Swal.fire({ title: 'Error', text: 'Unable to tokenize card with QuickBooks. Please try again.', icon: 'error', confirmButtonText: 'OK' });
                return;
            }

            const saveRes = await apiHandler(QUICKBOOK_CARD_SAVE, 'POST', {
                token: quickbookToken,
                name: newCardHolder.trim() || undefined,
                student_id: getEffectiveStudentId(profileData),
            });

            if (saveRes?.data?.response?.status !== 200) {
                Swal.fire({ title: 'Error', text: saveRes?.data?.response?.message || 'Unable to save card.', icon: 'error', confirmButtonText: 'OK' });
                return;
            }

            const refreshedCards = await getSavedQuickbookCards();
            setSavedCards(refreshedCards);
            setSelectedCardId(String(refreshedCards[0]?.id || ''));
            setPayModalView('pay');

            Swal.fire({ title: 'Card Added', text: saveRes?.data?.response?.message || 'Card tokenized and saved successfully.', icon: 'success', confirmButtonText: 'OK' });
        } catch (error) {
            const msg = error?.response?.data?.errors?.[0]?.message || 'Something went wrong while tokenizing card.';
            Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonText: 'OK' });
        } finally {
            setIsAddCardSubmitting(false);
        }
    };

    const handlePayPendingTransaction = async () => {
        if (!selectedCardId) {
            Swal.fire({ title: 'Card Required', text: 'Please select a saved card to continue.', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        if (!selectedTransaction?.id) {
            Swal.fire({ title: 'Invalid Transaction', text: 'Transaction id is missing.', icon: 'error', confirmButtonText: 'OK' });
            return;
        }

        setIsPaying(true);
        try {
            const selectedCard = savedCards.find((card) => String(card?.id) === String(selectedCardId));
            const cardToken = selectedCard?.quickbookCardId || selectedCard?.token || selectedCardId;

            let res;
            if (payModalType === 'class') {
                res = await apiHandler(CLASS_TRANSACTION_PAY, 'POST', {
                    class_transaction_id: selectedTransaction.id,
                    token: cardToken,
                    student_id: getEffectiveStudentId(profileData),
                });
            } else {
                const payload = {
                    grade_transaction_id: selectedTransaction.id,
                    payment_method: 0,
                    user_quickbook_card_id: selectedCardId,
                    student_id: getEffectiveStudentId(profileData),
                };
                if (selectedCard?.token) {
                    payload.token = selectedCard.token;
                }
                res = await apiHandler(GRADE_TRANSACTION_PAY, 'POST', payload);
            }

            if (res?.data?.response?.status === 200) {
                Swal.fire({ title: 'Payment Successful', text: res?.data?.data?.message || res?.data?.response?.message || 'Transaction paid successfully.', icon: 'success', confirmButtonText: 'OK' });
                closePayModal();
                if (payModalType === 'class') {
                    getClassTransactionsApi();
                } else {
                    getGradeTransactionsApi();
                }
            } else {
                Swal.fire({ title: 'Payment Failed', text: res?.data?.response?.message || 'Unable to process payment right now.', icon: 'error', confirmButtonText: 'OK' });
            }
        } catch (error) {
            Swal.fire({
                title: 'Payment Failed',
                text: error?.response?.data?.response?.message || 'Something went wrong while paying this transaction.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsPaying(false);
        }
    };

    async function getGradeTransactionsApi() {
        setShowMoreDataLoader(true)
        try {
            const res = await apiHandler(GRADE_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
                student_id: getEffectiveStudentId(profileData),
            })
            if (res.data?.response?.status === 200) {
                setGradeTransactions((res?.data?.data?.grade?.data || []).sort((left, right) => new Date(right?.created_at || right?.payment_date || 0) - new Date(left?.created_at || left?.payment_date || 0)))
            } else {
                //navigate('/auth/sign-in')
            }
        } catch (error) {
            console.error(error);
        }
        finally {
            setShowMoreDataLoader(false)
        }
    }

    async function getClassTransactionsApi() {
        setShowMoreDataLoader(true)
        try {
            const res = await apiHandler(CLASS_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
                student_id: getEffectiveStudentId(profileData),
            })
            if (res.data?.response?.status === 200) {
                setClassTransactions(res?.data?.data?.classe_transactions?.data || [])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setShowMoreDataLoader(false)
        }
    }

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const res = await apiHandler(GRADE_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
                student_id: getEffectiveStudentId(profileData),
            })
            if (res.data?.response?.status === 200) {
                setGradeTransactions((res?.data?.data?.grade?.data || []).sort((left, right) => new Date(right?.created_at || right?.payment_date || 0) - new Date(left?.created_at || left?.payment_date || 0)))
            } else {
                setGradeTransactions([])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setRefreshing(false)
        }
    }

    const handleClassRefresh = async () => {
        setClassRefreshing(true);
        try {
            const res = await apiHandler(CLASS_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
                student_id: getEffectiveStudentId(profileData),
            })
            if (res.data?.response?.status === 200) {
                setClassTransactions(res?.data?.data?.classe_transactions?.data || [])
            } else {
                setClassTransactions([])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setClassRefreshing(false)
        }
    }

    return (
        <>
            <ProfileHeader title="My Transactions" img={img6} />
            <div id="content-page" className="content-page">
                <Container>
                    <Tab.Container id="left-tabs-example" defaultActiveKey="first">
                        <Card className="p-0 mb-4">
                            <Card.Body className="p-0">
                                <div className="user-tabing">
                                    <Nav as="ul" variant="pills" className="d-flex align-items-center justify-content-center profile-feed-items p-0 m-0">
                                        <Nav.Item as="li" className="col-12 col-sm-6 p-0">
                                            <Nav.Link href="#grade-tab" eventKey="first" role="button" className="text-center p-3">Grade Transactions</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item as="li" className="col-12 col-sm-6 p-0">
                                            <Nav.Link href="#class-tab" eventKey="second" role="button" className="text-center p-3">Class Transactions</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </div>
                            </Card.Body>
                        </Card>

                        <Tab.Content>
                            <Tab.Pane eventKey="first">
                                <Row className="g-4">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                        <div>
                                            <h4 className="mb-1">Grade Transactions</h4>
                                            <p className="text-muted mb-0">Review your tuition payments, subsidy deductions, and status updates.</p>
                                        </div>
                                        <Button variant="outline-primary" onClick={handleRefresh} disabled={refreshing || showMoreDataLoader}>
                                            {refreshing ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Transactions</div>
                                    <h3 className="mb-0">{summary.totalTransactions}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Paid</div>
                                    <h3 className="mb-0">{formatCurrency(summary.totalPaid)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Subsidy</div>
                                    <h3 className="mb-0">{formatCurrency(summary.totalSubsidy)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Pending</div>
                                    <h3 className="mb-0">{summary.pendingCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 bg-white border-0 pt-4 px-4 pb-0">
                                    <div className="header-title">
                                        <h5 className="card-title mb-1">Transaction History</h5>
                                        <p className="text-muted mb-0">Latest transactions appear first.</p>
                                    </div>
                                    <Badge bg="primary" pill>{gradeTransactions.length} Records</Badge>
                                </Card.Header>
                                <Card.Body className="px-4 pb-4 pt-3">
                                    {showMoreDataLoader ? (
                                        <div className="d-flex justify-content-center align-items-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                        </div>
                                    ) : gradeTransactions?.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table align-middle mb-0">
                                                <thead>
                                                    <tr className="text-muted">
                                                        <th>Grade</th>
                                                        <th>Billing Date</th>
                                                        <th>Payment Method</th>
                                                        <th>Price</th>
                                                        <th>Subsidy</th>
                                                        <th>Total</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {gradeTransactions.map((transaction) => {
                                                        const statusMeta = getStatusMeta(transaction);
                                                        const canPay = isUnpaidTransaction(transaction);
                                                        return (
                                                            <tr key={transaction?.id}>
                                                                <td>
                                                                    <div className="fw-semibold">{transaction?.user_grade?.grade_name?.name || 'Grade'}</div>
                                                                    <small className="text-muted">{transaction?.user_grade?.grade_name?.slug || '-'}</small>
                                                                </td>
                                                                <td>{Moment(transaction?.payment_date || transaction?.created_at).format('MMM D, YYYY')}</td>
                                                                <td>{getPaymentTypeLabel(transaction?.user_grade?.grade_name?.payment_type)}</td>
                                                                <td>{formatCurrency(transaction?.price)}</td>
                                                                <td>{formatCurrency(transaction?.subsidy_amount)}</td>
                                                                <td className="fw-semibold">{formatCurrency(transaction?.total_amount)}</td>
                                                                <td>
                                                                    <Badge bg={statusMeta.variant} className="d-inline-flex align-items-center gap-1">
                                                                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{statusMeta.icon}</span>
                                                                        {statusMeta.label}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    {canPay ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openPayModal(transaction)}
                                                                            style={payNowButtonStyle}
                                                                        >
                                                                            Pay Now
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <NoDataFound txtData={"Transactions Not Found!"} />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                                </Row>
                            </Tab.Pane>

                            <Tab.Pane eventKey="second">
                                <Row className="g-4">
                                    <Col lg={12}>
                                        <Card className="border-0 shadow-sm">
                                            <Card.Body className="p-4">
                                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                                    <div>
                                                        <h4 className="mb-1">Class Transactions</h4>
                                                        <p className="text-muted mb-0">Review your class payments and pay any pending balance.</p>
                                                    </div>
                                                    <Button variant="outline-primary" onClick={handleClassRefresh} disabled={classRefreshing || showMoreDataLoader}>
                                                        {classRefreshing ? 'Refreshing...' : 'Refresh'}
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col lg={4} md={6}>
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Body>
                                                <div className="text-muted mb-2">Total Transactions</div>
                                                <h3 className="mb-0">{classSummary.totalTransactions}</h3>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col lg={4} md={6}>
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Body>
                                                <div className="text-muted mb-2">Total Paid</div>
                                                <h3 className="mb-0">{formatCurrency(classSummary.totalPaid)}</h3>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col lg={4} md={6}>
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Body>
                                                <div className="text-muted mb-2">Pending</div>
                                                <h3 className="mb-0">{classSummary.pendingCount}</h3>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col sm={12}>
                                        <Card className="border-0 shadow-sm">
                                            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 bg-white border-0 pt-4 px-4 pb-0">
                                                <div className="header-title">
                                                    <h5 className="card-title mb-1">Transaction History</h5>
                                                    <p className="text-muted mb-0">Latest transactions appear first.</p>
                                                </div>
                                                <Badge bg="primary" pill>{classTransactions.length} Records</Badge>
                                            </Card.Header>
                                            <Card.Body className="px-4 pb-4 pt-3">
                                                {showMoreDataLoader ? (
                                                    <div className="d-flex justify-content-center align-items-center py-5">
                                                        <Spinner animation="border" variant="primary" />
                                                    </div>
                                                ) : classTransactions?.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table align-middle mb-0">
                                                            <thead>
                                                                <tr className="text-muted">
                                                                    <th>Class</th>
                                                                    <th>Payment Date</th>
                                                                    <th>Payment Method</th>
                                                                    <th>Price</th>
                                                                    <th>Status</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {classTransactions.map((transaction) => {
                                                                    const statusMeta = getStatusMeta(transaction);
                                                                    const canPay = isUnpaidTransaction(transaction);
                                                                    return (
                                                                        <tr key={transaction?.id}>
                                                                            <td>
                                                                                <Link to={`/dashboards/app/class-detail/${transaction?.class_name?.slug}`} className="text-decoration-none fw-semibold">
                                                                                    {transaction?.class_name?.name || 'Class'}
                                                                                </Link>
                                                                            </td>
                                                                            <td>{Moment(transaction?.class_student?.last_payment_date || transaction?.created_at).format('MMM D, YYYY')}</td>
                                                                            <td>{Number(transaction?.class_student?.payment_type) === 0 ? 'Weekly Payment' : Number(transaction?.class_student?.payment_type) === 1 ? 'Monthly Payment' : 'One Time Payment'}</td>
                                                                            <td className="fw-semibold">{formatCurrency(transaction?.total_amount || transaction?.price)}</td>
                                                                            <td>
                                                                                <Badge bg={statusMeta.variant} className="d-inline-flex align-items-center gap-1">
                                                                                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{statusMeta.icon}</span>
                                                                                    {statusMeta.label}
                                                                                </Badge>
                                                                            </td>
                                                                            <td>
                                                                                {canPay ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => openPayModal(transaction, 'class')}
                                                                                        style={payNowButtonStyle}
                                                                                    >
                                                                                        Pay Now
                                                                                    </button>
                                                                                ) : (
                                                                                    <span className="text-muted">-</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <NoDataFound txtData={"Transactions Not Found!"} />
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Container>
            </div>

            <Modal show={showPayModal} onHide={closePayModal} centered backdrop={isPaying || isAddCardSubmitting ? 'static' : true}>
                <Modal.Header closeButton={!isPaying && !isAddCardSubmitting} className="border-0 pb-0">
                    <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '16px', fontWeight: 700 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 22, color: ACCENT }}>payments</span>
                        Complete Payment
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <div style={{ background: '#fff7ed', border: '1px solid #fde3cf', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>
                                    {payModalType === 'class' ? 'Class' : 'Grade'}
                                </div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>
                                    {payModalType === 'class'
                                        ? (selectedTransaction?.class_name?.name || 'Class')
                                        : (selectedTransaction?.user_grade?.grade_name?.name || 'Grade')}
                                </div>
                            </div>
                            <div className="text-end">
                                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>Amount Due</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: ACCENT }}>
                                    {formatCurrency(selectedTransaction?.total_amount || selectedTransaction?.price)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 10, padding: 4, marginBottom: 18 }}>
                        <button
                            type='button'
                            onClick={() => setPayModalView('pay')}
                            disabled={isPaying || isAddCardSubmitting}
                            style={{
                                flex: 1, border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 13, fontWeight: 700,
                                background: payModalView === 'pay' ? '#fff' : 'transparent',
                                color: payModalView === 'pay' ? ACCENT : '#6b7280',
                                boxShadow: payModalView === 'pay' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                            }}
                        >
                            Saved Cards
                        </button>
                        <button
                            type='button'
                            onClick={() => setPayModalView('add-card')}
                            disabled={isPaying || isAddCardSubmitting}
                            style={{
                                flex: 1, border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 13, fontWeight: 700,
                                background: payModalView === 'add-card' ? '#fff' : 'transparent',
                                color: payModalView === 'add-card' ? ACCENT : '#6b7280',
                                boxShadow: payModalView === 'add-card' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                            }}
                        >
                            + Add New Card
                        </button>
                    </div>

                    {payModalView === 'pay' ? (
                        savedCards.length === 0 ? (
                            <div className="text-center" style={{ background: '#fef2f2', borderRadius: 10, padding: '24px 16px' }}>
                                <span className="material-symbols-outlined d-block mb-1" style={{ fontSize: 28, color: '#b91c1c' }}>credit_card_off</span>
                                <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>No saved cards found</div>
                                <div style={{ fontSize: 12, color: '#9ca3af' }}>Switch to "Add New Card" to add one.</div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {savedCards.map((card) => {
                                    const selected = String(selectedCardId) === String(card.id);
                                    return (
                                        <div
                                            key={card.id}
                                            role="button"
                                            onClick={() => !isPaying && setSelectedCardId(String(card.id))}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                                border: selected ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                                                background: selected ? '#fff7ed' : '#fff',
                                                borderRadius: 10, cursor: isPaying ? 'default' : 'pointer',
                                            }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: 22, color: selected ? ACCENT : '#9ca3af' }}>credit_card</span>
                                            <div className="flex-grow-1">
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
                                                    {card.name || card.brand || 'Card'} {card.last4 ? `•••• ${card.last4}` : ''}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                    Expires {card.expMonth || '--'}/{card.expYear || '--'}
                                                </div>
                                            </div>
                                            <span className="material-symbols-outlined" style={{ fontSize: 20, color: selected ? ACCENT : '#d1d5db' }}>
                                                {selected ? 'radio_button_checked' : 'radio_button_unchecked'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        <>
                            <Form.Group className='mb-2'>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Card Holder Name</Form.Label>
                                <Form.Control
                                    type='text'
                                    size='sm'
                                    placeholder='Full name on card'
                                    value={newCardHolder}
                                    onChange={(e) => setNewCardHolder(e.target.value)}
                                    disabled={isAddCardSubmitting}
                                />
                            </Form.Group>
                            <Form.Group className='mb-2'>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Card Number</Form.Label>
                                <Form.Control
                                    type='text'
                                    size='sm'
                                    placeholder='1234 5678 9012 3456'
                                    value={newCardNumber}
                                    onChange={(e) => setNewCardNumber(formatCardInput(e.target.value))}
                                    disabled={isAddCardSubmitting}
                                />
                            </Form.Group>
                            <Row className='g-2 mb-2'>
                                <Col xs={4}>
                                    <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Month</Form.Label>
                                    <Form.Select size='sm' value={newCardExpMonth} onChange={(e) => setNewCardExpMonth(e.target.value)} disabled={isAddCardSubmitting}>
                                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => <option key={m} value={m}>{m}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col xs={4}>
                                    <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Year</Form.Label>
                                    <Form.Select size='sm' value={newCardExpYear} onChange={(e) => setNewCardExpYear(e.target.value)} disabled={isAddCardSubmitting}>
                                        {Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + i)).map((y) => <option key={y} value={y}>{y}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col xs={4}>
                                    <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>CVV</Form.Label>
                                    <Form.Control
                                        type='password'
                                        size='sm'
                                        placeholder='123'
                                        value={newCardCvv}
                                        onChange={(e) => setNewCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                        disabled={isAddCardSubmitting}
                                    />
                                </Col>
                            </Row>
                            <Form.Group className='mb-3'>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>ZIP / Postal Code</Form.Label>
                                <Form.Control
                                    type='text'
                                    size='sm'
                                    placeholder='ZIP'
                                    value={newCardZip}
                                    onChange={(e) => setNewCardZip(e.target.value)}
                                    disabled={isAddCardSubmitting}
                                />
                            </Form.Group>
                            <Button
                                size='sm'
                                className='w-100 d-flex align-items-center justify-content-center gap-2'
                                style={{ background: ACCENT, borderColor: ACCENT, fontWeight: 700 }}
                                onClick={addCardAndContinue}
                                disabled={isAddCardSubmitting}
                            >
                                {isAddCardSubmitting && <Spinner animation="border" size="sm" />}
                                {isAddCardSubmitting ? 'Saving card...' : 'Save Card & Continue'}
                            </Button>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 flex-column align-items-stretch gap-2">
                    {payModalView === 'pay' && (
                        <Button
                            className='w-100 d-flex align-items-center justify-content-center gap-2'
                            style={{ background: ACCENT, borderColor: ACCENT, fontWeight: 700 }}
                            onClick={handlePayPendingTransaction}
                            disabled={isPaying || !selectedCardId || savedCards.length === 0}
                        >
                            {isPaying && <Spinner animation="border" size="sm" />}
                            {isPaying ? 'Processing Payment...' : `Pay ${formatCurrency(selectedTransaction?.total_amount || selectedTransaction?.price)}`}
                        </Button>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center gap-1" style={{ fontSize: 11, color: '#9ca3af' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>lock</span>
                            Secured by QuickBooks Payments
                        </span>
                        <Button variant="link" size="sm" className="text-muted text-decoration-none p-0" onClick={closePayModal} disabled={isPaying || isAddCardSubmitting}>
                            Cancel
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    )

}

export default MyTransactions;